import { fadeIn, hide, show, slideDown, slideUp } from "../Helpers/animations";
import { createElement } from "../Helpers/createElement";

const initSubMenuTogglesHandler = () => {
    const sideMenuToggle = document.querySelector('.side-menu .menu-toggle .menu-toggle-btn') as HTMLButtonElement | null;
    sideMenuToggle?.addEventListener('click', () => {
        if (sideMenuToggle.classList.contains('opened')) {
            sideMenuToggle.classList.remove('opened');
            sideMenuToggle.ariaExpanded = 'false';
            slideUp(sideMenuToggle.ariaControlsElements?.[0] as HTMLElement | null ?? null, 200);
        } else {
            sideMenuToggle.classList.add('opened');
            sideMenuToggle.ariaExpanded = 'true';
            slideDown(sideMenuToggle.ariaControlsElements?.[0] as HTMLElement | null ?? null, 200);
        }
    });
}

const initSubMenuListToggleHandlers = () => {
    document.querySelectorAll('.side-menu .menu-lists .parent').forEach(parentListItem => {
        const toggle = parentListItem.querySelector('button');
        toggle?.addEventListener('click', () => {
            if (parentListItem.classList.contains('opened')) {
                parentListItem.classList.remove('opened');
                toggle.ariaExpanded = 'false';
                slideUp(toggle.ariaControlsElements?.[0] as HTMLElement | null, 200);
            } else {
                parentListItem.classList.add('opened');
                toggle.ariaExpanded = 'true';
                slideDown(toggle.ariaControlsElements?.[0] as HTMLElement | null, 200);
            }
        });
    });
};

const initBoxTabsHandler = () => {
    document.querySelectorAll('.box-tabs').forEach(boxTabContainer => {
        const tabs = boxTabContainer.querySelectorAll('.tabs .tab') as NodeListOf<HTMLDivElement>;
        tabs.forEach(tab => {
            if (tab !== tab.parentElement?.firstElementChild) {
                tab.style.display = 'none';
            }
        });

        const nav = boxTabContainer.querySelector('.box-tab-nav');
        if (nav) {
            const toggles = nav?.querySelectorAll('a');
            toggles.forEach(toggle =>
                toggle.addEventListener('click', event => {
                    event.preventDefault();
                    const target = document.getElementById(toggle.href);
                    toggles.forEach(link => link.classList.remove('on'));
                    toggle.classList.add('on');
                    tabs.forEach(tab => tab.style.display = 'none');
                    fadeIn(target, 200);
                })
            );
        }
    });
};

const initChecklistHandler = () => {
    (document.querySelectorAll('.list-checklist li a') as NodeListOf<HTMLAnchorElement>).forEach(toggle =>
        toggle?.addEventListener('click', event => {
            event.preventDefault();
            toggle.classList.contains('on') ?
                toggle.classList.remove('on') :
                toggle.classList.add('on');
        })
    );
};

const initFilterBoxHandler = () => {
    (document.querySelectorAll('.filter-box .toggle') as NodeListOf<HTMLAnchorElement>).forEach(toggle =>
        toggle.addEventListener('click', event => {
            event.preventDefault();
            if (toggle.classList.contains('opened')) {
                toggle.classList.remove('opened');
                slideUp(toggle.nextElementSibling as HTMLElement);
            } else {
                toggle.classList.add('opened');
                slideDown(toggle.nextElementSibling as HTMLElement);
            }
        })
    );
};

const initJumpLinksHandlers = () => {
    (document.querySelectorAll('a.scroll-to') as NodeListOf<HTMLAnchorElement>).forEach(link =>
        link.addEventListener('click', event => {
            event.preventDefault();
            const match = link.href.match(/#[^#\/]*$/);

            if (match?.[0]) {
                document.querySelector(match[0])?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        })
    );

    document.querySelector('a.skip-to-content')?.addEventListener('click', event => {
        event.preventDefault();
        document.querySelector('#content')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        (document.querySelector('#content a') as HTMLAnchorElement)?.focus();
    });
};

const initEmbedSizingHandler = () => {
    (document.querySelectorAll('.fit-embed-to-container > iframe') as NodeListOf<HTMLIFrameElement>).forEach(iframe => {
        const height = parseInt(iframe.getAttribute('height') ?? '0');
        const width = parseInt(iframe.getAttribute('width') ?? '0');

        if (height && width) {
            (iframe.parentElement as HTMLDivElement).style.paddingBottom = `${(height / width * 100).toFixed(2)}%`;
        } else {
            (iframe.parentElement as HTMLDivElement).style.paddingBottom = '56.25%';
        }
    });
};

const initTableHandlers = () => {
    (document.querySelectorAll('.body-copy table') as NodeListOf<HTMLTableElement>).forEach(table => {
        if (!table.classList.contains('static') && !(table.parentElement as HTMLDivElement).classList.contains('responsive-table')) {
            const wrapper = createElement('div', {
                classes: ['responsive-table']
            });
            
            const parent = table.parentElement;
            const nextSibling = table.nextElementSibling;

            wrapper.appendChild(table);
            if (nextSibling) {
                parent?.insertBefore(wrapper, nextSibling);
            } else {
                parent?.appendChild(wrapper);
            }
        }
    });

    const responsiveTableContainers = document.querySelectorAll('.responsive-table') as NodeListOf<HTMLDivElement>;
    responsiveTableContainers.forEach(tableContainer =>
        tableContainer.scrollWidth > tableContainer.offsetWidth && tableContainer.classList.add('active')
    );

    window.addEventListener('resize', () => {
        responsiveTableContainers.forEach(tableContainer => 
            tableContainer.scrollWidth > tableContainer.offsetWidth ?
                tableContainer.classList.add('active') :
                tableContainer.classList.remove('active')
        )
    });
};

const initScrollHandler = () => {
    window.addEventListener('scroll', () =>
        window.scrollY > 0 ?
            document.getElementById('page')?.classList.remove('top') :
            document.getElementById('page')?.classList.add('top')
    );
};

const initSwitchWrapHandlers = () => {
    const tabListContainer = document.querySelector('.switch-wrap') as HTMLDivElement | null;
    const tabPanels = [...document.querySelectorAll('.switch-content-wrap .switch-content') as NodeListOf<HTMLDivElement>];

    if (tabListContainer) {
        const tabButtons = [...tabListContainer.querySelectorAll('button') as NodeListOf<HTMLButtonElement>];

        tabListContainer.addEventListener('keydown', event => {
            let previousActiveTabIndex = -1;
            let newActiveTabIndex = -1;
            
            switch (event.key) {
                case 'ArrowLeft': 
                    event.preventDefault();
                    previousActiveTabIndex = tabButtons.findIndex(button => button.ariaSelected === 'true');
                    newActiveTabIndex = previousActiveTabIndex - 1 >= 0 ? previousActiveTabIndex - 1 : tabButtons.length - 1;
                    
                    resetTabButtons();
                    activateTabButton(tabButtons[newActiveTabIndex]);
                    break;      
                case 'ArrowRight':
                    event.preventDefault();
                    previousActiveTabIndex = tabButtons.findIndex(button => button.ariaSelected === 'true');
                    newActiveTabIndex = previousActiveTabIndex + 1 < tabButtons.length ? previousActiveTabIndex + 1 : 0;        

                    resetTabButtons();
                    activateTabButton(tabButtons[newActiveTabIndex]);
                    break;
                case 'End': 
                    event.preventDefault();
                    resetTabButtons();
                    activateTabButton(tabButtons[tabButtons.length - 1]);
                    break;
                case 'Home':
                    event.preventDefault();
                    resetTabButtons();
                    activateTabButton(tabButtons[0]);
                    break;
                default:
                    return;
            }
        });

        tabButtons.forEach(button => button.addEventListener('click', () => {
            resetTabButtons();
            activateTabButton(button);
        }));

        const resetTabButtons = () => {
            tabButtons.forEach(button => {
                button.tabIndex = -1;
                button.removeAttribute('aria-selected');
            });
        };
        
        const activateTabButton = (button: HTMLButtonElement | null | undefined) => {
            if (button) {
                button.ariaSelected = 'true';
                button.removeAttribute('tabindex');
                button.focus();
                toggleMenu(button.ariaControlsElements?.[0]);              
            }
        };

        const toggleMenu = (menuToShow: Element | null | undefined) => {
            if (menuToShow) {            
                const menusToHide = tabPanels.filter(panel => panel.id !== menuToShow.id);
                menusToHide.forEach(panel => hide(panel));
                show(menuToShow as HTMLElement);
            }
        };
    }
};

export const initCommonHandlers = () => {
    initSubMenuTogglesHandler();
    initSubMenuListToggleHandlers();
    initBoxTabsHandler();
    initChecklistHandler();
    initFilterBoxHandler();
    initJumpLinksHandlers();
    initEmbedSizingHandler();
    initTableHandlers();
    initScrollHandler();
    initSwitchWrapHandlers();
};